// Co-op invitations on the client: keeps the player's invitations up to date and tells the UI when something arrives or changes.
// (Server: server/invites.js; plan: docs/COOP-INVITES-PLAN.md.)
//
//   getInvites()          → { received, sent, blocked, rules, stays, credits, coop, loaded, unavailable } (stays + credits: the visits this player hosts / is on, and the coin the server has credited them — see game/coopManager.js)
//   attention()           → what needs a pop-up now: pending invitations not yet dismissed this session, and expired ones the player has not been told about
//   refresh()             → ask the server now (also called by the poll)
//   setPollMode(mode)     → how often to look: SLOW (20 s, normal) · ENGAGED (4 s, a guest is in someone's world — later steps) · PURSUIT (3 s, a theft is under way)
//   inviteActions         → accept / decline / cancel / block / unblock / create / dismiss
//   proposals + marriages (docs/MARRIAGE-PLAN.md) ride on the same poll: getInvites().proposals / .marriages, attentionProposals(), proposalActions (same verbs, plus divorce),
//   getInboxUnread() = marriage proposals waiting for an answer (the dot on the Registry button), and Events.HouseholdDue once per sign-in for a husband
//
// While signed out nothing is fetched. A server that has not been set up for invitations yet answers 503: the manager then stays quiet (unavailable = true).

import { Events, on, emit } from '../ui/eventBus.js'
import { inviteApi, proposalApi } from './accountManager.js'
import { getProfile } from './state.js'

export const POLL = Object.freeze({ SLOW: 20_000, ENGAGED: 4_000, PURSUIT: 3_000 })
const DISMISS_KEY = 'mao-kingdom/invites/dismissed/v1' // per browser tab session: "decide later" must not nag again until the next sign-in
const PDISMISS_KEY = 'mao-kingdom/proposals/dismissed/v1'
const REPORT_KEY = 'mao-kingdom/household/reported/v1' // the husband's report is shown once per sign-in (per browser tab session)

const blank = () => ({
  received: [], sent: [], blocked: [], rules: null, stays: [], credits: { total: 0 }, court: null, coop: null, loaded: false, unavailable: false,
  proposals: { received: [], sent: [], blocked: [] }, marriages: { wives: [], husband: null, ended: [] }, marriageRules: null, marriageSetup: false,
})
let state = blank()
let unread = 0
let mode = 'SLOW'
let timer = null
let signedIn = false
let inFlight = null
let serverSkew = 0 // (this device's clock minus the server's: countdowns use the server's times)
export const getSkew = () => serverSkew
let sentStatus = new Map() // invitation id → status last seen, to notice "they accepted / declined"
let proposalStatus = new Map() // the same for the marriage proposals this player sent

const readSet = (key) => { try { return new Set(JSON.parse(sessionStorage.getItem(key) || '[]')) } catch { return new Set() } }
const writeSet = (key, set) => { try { sessionStorage.setItem(key, JSON.stringify([...set].slice(-100))) } catch { /* private mode: fine */ } }
const readDismissed = () => readSet(DISMISS_KEY)
const writeDismissed = (set) => writeSet(DISMISS_KEY, set)

export const getInvites = () => state
export function attention() {
  const dismissed = readDismissed()
  return state.received
    .filter((i) => (i.status === 'pending' && !dismissed.has(i.id)) || (i.status === 'expired' && i.seenAt == null && !dismissed.has(i.id)))
    .sort((a, b) => (a.status === 'pending' ? 0 : 1) - (b.status === 'pending' ? 0 : 1) || a.createdAt - b.createdAt)
}

// Marriage proposals that need a pop-up now: pending ones not put off this session, and expired ones the player was never told about.
export function attentionProposals() {
  const dismissed = readSet(PDISMISS_KEY)
  return state.proposals.received
    .filter((p) => (p.status === 'pending' && !dismissed.has(p.id)) || (p.status === 'expired' && p.seenAt == null && !dismissed.has(p.id)))
    .sort((a, b) => (a.status === 'pending' ? 0 : 1) - (b.status === 'pending' ? 0 : 1) || a.createdAt - b.createdAt)
}
// How many proposals are waiting for this player's answer (the red dot on the Registry button).
export const getInboxUnread = () => unread
function updateUnread() {
  const n = state.proposals.received.filter((p) => p.status === 'pending').length
  if (n !== unread) { unread = n; emit(Events.InboxUnreadChange, { unread }) }
}

// A husband is shown his household report once per sign-in (unless he switched it off in the report).
export const reportWanted = () => { try { return getProfile().householdReport !== false } catch { return true } }
function maybeHouseholdDue() {
  if (!state.marriages.wives.length || !reportWanted()) return
  try { if (sessionStorage.getItem(REPORT_KEY)) return; sessionStorage.setItem(REPORT_KEY, '1') } catch { /* private mode: show it each time the page loads */ }
  emit(Events.HouseholdDue, { wives: state.marriages.wives.length })
}

function schedule() {
  clearTimeout(timer); timer = null
  if (!signedIn) return
  timer = setTimeout(async () => { await refresh(); schedule() }, POLL[mode])
}
export function setPollMode(next) { if (POLL[next] && next !== mode) { mode = next; schedule(); refresh() } }
export const getPollMode = () => mode

let again = false // a refresh was asked for while one was already running: run once more when it finishes, so an action's result is never missed
export async function refresh() {
  if (!signedIn) return state
  if (inFlight) { again = true; return inFlight }
  inFlight = (async () => {
    try {
      const t0 = Date.now()
      const box = await inviteApi.list()
      if (box.now) serverSkew = Math.round((t0 + Date.now()) / 2 - box.now)
      // who answered one of my invitations / marriage proposals since last time?
      const changed = box.sent.filter((i) => sentStatus.has(i.id) && sentStatus.get(i.id) !== i.status && ['accepted', 'declined'].includes(i.status))
      const wed = { proposals: box.proposals ?? blank().proposals, marriages: box.marriages ?? blank().marriages, marriageRules: box.marriageRules ?? null, marriageSetup: !!box.marriageSetup }
      const changedProposals = wed.proposals.sent.filter((p) => proposalStatus.has(p.id) && proposalStatus.get(p.id) !== p.status && ['accepted', 'declined'].includes(p.status))
      const firstLoad = !state.loaded
      const prev = new Set(state.received.map((i) => i.id)), prevProp = new Set(state.proposals.received.map((p) => p.id))
      state = { received: box.received, sent: box.sent, blocked: box.blocked, rules: box.rules, stays: box.stays ?? [], credits: box.credits ?? { total: 0 }, court: box.court ?? null, coop: box.coop ?? null, loaded: true, unavailable: false, ...wed }
      sentStatus = new Map(box.sent.map((i) => [i.id, i.status]))
      proposalStatus = new Map(wed.proposals.sent.map((p) => [p.id, p.status]))
      emit(Events.InvitesChange, { ...state, attention: attention(), proposalAttention: attentionProposals(), changedSent: changed, changedProposals, arrived: firstLoad ? [] : box.received.filter((i) => !prev.has(i.id)), arrivedProposals: firstLoad ? [] : wed.proposals.received.filter((p) => !prevProp.has(p.id)) })
      updateUnread()
      if (firstLoad) maybeHouseholdDue()
    } catch (err) {
      if (err?.status === 503 && err.body?.setup) { state = { ...state, unavailable: true, loaded: true }; emit(Events.InvitesChange, { ...state, attention: [], proposalAttention: [], changedSent: [], changedProposals: [], arrived: [], arrivedProposals: [] }) }
      // anything else (offline, a hiccup): keep what we have and try again at the next tick
    } finally { inFlight = null }
    if (again) { again = false; return refresh() }
    return state
  })()
  return inFlight
}

function changed() { return refresh() }
export const inviteActions = {
  create: async (body) => { const r = await inviteApi.create(body); await changed(); return r },
  accept: async (id) => { const r = await inviteApi.accept(id); await changed(); if (r.stay) emit(Events.CoopVisitStart, { stay: r.stay }); return r }, // (accepting starts the stay: the game travels to the host's kingdom)
  decline: async (id) => { const r = await inviteApi.decline(id); await changed(); return r },
  cancel: async (id) => { const r = await inviteApi.cancel(id); await changed(); return r },
  block: async (hostId) => { const r = await inviteApi.block(hostId); await changed(); return r },
  unblock: async (hostId) => { const r = await inviteApi.unblock(hostId); await changed(); return r },
  // "Decide later" for a pending invitation; for an expired one the server is told it has been shown
  dismiss: async (invite) => {
    const set = readDismissed(); set.add(invite.id); writeDismissed(set)
    if (invite.status === 'expired' && invite.seenAt == null) { try { await inviteApi.seen([invite.id]) } catch { /* shown anyway */ } await changed() } else emit(Events.InvitesChange, { ...state, attention: attention(), proposalAttention: attentionProposals(), changedSent: [], changedProposals: [], arrived: [], arrivedProposals: [] })
  },
}

export const proposalActions = {
  create: async (body) => { const r = await proposalApi.create(body); await changed(); return r },
  accept: async (id) => { const r = await proposalApi.accept(id); await changed(); return r },
  decline: async (id) => { const r = await proposalApi.decline(id); await changed(); return r },
  cancel: async (id) => { const r = await proposalApi.cancel(id); await changed(); return r },
  block: async (proposerId) => { const r = await proposalApi.block(proposerId); await changed(); return r },
  unblock: async (proposerId) => { const r = await proposalApi.unblock(proposerId); await changed(); return r },
  divorce: async (marriageId) => { const r = await proposalApi.divorce(marriageId); await changed(); return r },
  // "Decide later" for a pending proposal; for an expired one the server is told it has been shown
  dismiss: async (p) => {
    const set = readSet(PDISMISS_KEY); set.add(p.id); writeSet(PDISMISS_KEY, set)
    if (p.status === 'expired' && p.seenAt == null) { try { await proposalApi.seen([p.id]) } catch { /* shown anyway */ } await changed() } else emit(Events.InvitesChange, { ...state, attention: attention(), proposalAttention: attentionProposals(), changedSent: [], changedProposals: [], arrived: [], arrivedProposals: [] })
  },
}

on(Events.AccountReady, ({ loggedIn }) => {
  const was = signedIn
  signedIn = !!loggedIn
  if (signedIn && !was) { writeDismissed(new Set()); writeSet(PDISMISS_KEY, new Set()); try { sessionStorage.removeItem(REPORT_KEY) } catch { /* fine */ } state = blank(); sentStatus = new Map(); proposalStatus = new Map(); refresh().then(schedule) } // a fresh sign-in: pop-ups start over
  if (!signedIn) { clearTimeout(timer); timer = null; state = blank(); unread = 0; emit(Events.InboxUnreadChange, { unread: 0 }) }
})
if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => { if (!document.hidden && signedIn) refresh() })
